/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { partition } from "lodash-es";
import { useStoreState } from "pullstate";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Amount } from "types/amount";
import { MODAL_SELECTORS } from "../../components/Modal/instance/modalSelectors";
import { ReactComponent as WalletIcon } from "../../img/wallet.svg";
import { ApprovalStore } from "../../store/approvals/ApprovalsStore";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Allowance } from "../../store/global/getAllowances";
import { OrderStatus } from "../../types/orderStatus";
import { getTokensToApprove, shouldRevokeFirst } from "../../utils/allowanceUtils";
import { isUserUSBased } from "../../utils/regionRestrictUtils";
import { Button } from "../../components/general/Button/Button";
import { Text } from "../../components/general/Text";
import { validateOrderEntry } from "../../utils/intialActionButtonUtils";
import useSignOrder from "../../hooks/trade/useSignOrder";
import LoadingEllipse from "../../components/ButterComponents/LoadingEllipse/LoadingEllipse";
import { Spinner } from "../../components/Spinner/Spinner";
import { withIsVisible } from "../../utils/reactUtils";
import {
  ActionButtonStateKeys,
  BUTTON_DISABLED_MAP,
  BUTTON_MODAL_MAP,
  INITIAL_BUTTON_TEXT_MAP,
  InitialActionButtonState,
  PROGRESSION_STATES,
} from "../../types/initialActionButtonTypes";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/browser";
import { Hash, parseUnits } from "viem";
import { waitForTransaction } from "wagmi/actions";
import { getTokenDecimals } from "../../utils/tokenUtils";
import { getWrappedSymbolForChain } from "../../utils/chainUtils";
import { useWrap } from "../../hooks/useWrap";
import { useTokenListByChain } from "../../hooks/useTokenListByChain";
import { getBalances } from "../../store/global/getBalances";

export const InitialActionButton = withIsVisible(() => {
  const [errorState, setErrorState] = useState<ActionButtonStateKeys | undefined>(undefined);
  const isProcessingRequestRef = useRef(false);
  const [buttonText, setButtonText] = useState<string>("");
  const state = useStoreState(GlobalStore);
  const signOrder = useSignOrder();
  const wrap = useWrap();
  const tokenList = useTokenListByChain();

  useEffect(() => {
    setErrorState(validateOrderEntry(state));
  }, [state]);

  useEffect(() => {
    if (!errorState) {
      return;
    } else if (errorState === "WrappingQuote") {
      setButtonText(`Wrap ${state.network.nativeCurrency.symbol} to trade`);
    } else if (errorState in INITIAL_BUTTON_TEXT_MAP) {
      const text = INITIAL_BUTTON_TEXT_MAP[errorState] as string;
      setButtonText(text);
    } else if (errorState === "RequestingQuote" || errorState === "WarningRequestingQuote") {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorState]);

  const confirmWrapOrUnwrap = useCallback(async () => {
    if (state.wrapQuote === undefined) {
      Sentry.captureException(`Attempting to wrap/unwrap with undefined wrapQuote`);
      return;
    }
    if (state.wrapQuote.amount === undefined) {
      Sentry.captureException(`Attempting to wrap/unwrap with undefined amount`);
      return;
    }

    const amount = parseUnits(
      state.wrapQuote.amount.value.toString() as `${number}`,
      getTokenDecimals(getWrappedSymbolForChain(state.network), tokenList)
    );
    let txHash: Hash | undefined = undefined;
    try {
      txHash = await wrap(amount);
    } catch (e) {
      console.error(`Error wrapping/unwraping`, e);
      GlobalStore.update(s => {
        s.orderStatus = OrderStatus.ENTRY;
      });
      Sentry.captureException(`Error wrapping/unwraping`, {
        extra: {
          error: e,
        },
      });
    }

    if (txHash) {
      setErrorState(InitialActionButtonState.InProgress as ActionButtonStateKeys);
      GlobalStore.update(s => {
        s.orderStatus = OrderStatus.IN_PROGRESS;
        if (s.wrapQuote) {
          s.wrapQuote = {
            ...s.wrapQuote,
            hash: txHash,
          };
        }
      });
      try {
        const transaction = await waitForTransaction({
          hash: txHash,
        });
        if (transaction?.status === "success") {
          if (tokenList) {
            // Update balances to reflect the new wrapped token balance
            await getBalances.run({
              address: state.address as Hash,
              tokenList,
              chain: state.network,
            });
          }
          GlobalStore.update(s => {
            s.orderStatus = OrderStatus.COMPLETED;
            isProcessingRequestRef.current = false;
          });
        }
      } catch (error) {
        isProcessingRequestRef.current = false;
        console.log("Wrap/unwrap tx did not complete");
        console.error(error);
        GlobalStore.update(s => {
          s.orderStatus = OrderStatus.ERROR;
        });
      }
    }
  }, [state.wrapQuote, state.network, state.address, tokenList, wrap]);

  const isApprovalNeeded = useCallback(() => {
    const requiredSignatures = state.quote?.requiredSignatures ?? [];
    const tokensToApprove = getTokensToApprove(state.quote, state.allowances);
    const approvalTarget = state.quote?.approvalTarget;
    const hasSignatures = !!(state.permits.permit1.signature || state.permits.permit2.signature);
    return (
      (tokensToApprove.length > 0 && state.permitType === "permit2" && approvalTarget) ||
      (requiredSignatures.length > 0 && approvalTarget && !hasSignatures)
    );
  }, [
    state.allowances,
    state.permitType,
    state.permits.permit1.signature,
    state.permits.permit2.signature,
    state.quote,
  ]);

  const handleOnClick = useCallback(async () => {
    if (errorState && PROGRESSION_STATES.includes(errorState)) {
      // Check if allowances need to be approved
      const isUSBased = await isUserUSBased();
      if (isUSBased) {
        GlobalStore.update(s => {
          s.modalSelector = MODAL_SELECTORS.REGION_RESTRICTED;
        });
        return;
      }
      const requiredSignatures = state.quote?.requiredSignatures ?? [];
      const tokensToApprove = getTokensToApprove(state.quote, state.allowances);
      const approvalTarget = state.quote?.approvalTarget;
      if (isApprovalNeeded()) {
        const [revokeFirst, increase] = partition(
          tokensToApprove,
          (
            entry: Allowance & {
              needed: Amount;
            }
          ) => shouldRevokeFirst(entry.symbol, state.allowances)
        );
        ApprovalStore.update(s => {
          s.approvalTarget = approvalTarget;
          s.requiredSignatures = requiredSignatures;
          s.tokensToApprove = tokensToApprove;
          s.initialAllowances = state.allowances;
          s.currentAllowances = state.allowances;
          s.tokensByFlow = {
            approve: increase,
            revoke: revokeFirst,
          };
          s.permitType = state.permitType;
          s.tokenLists = state.tokenLists ?? {};
        });

        GlobalStore.update(s => {
          s.orderStatus = OrderStatus.APPROVALS;
        });
        return;
      } else {
        GlobalStore.update(s => {
          s.orderStatus = OrderStatus.CONFIRMING;
        });
        if (
          errorState === InitialActionButtonState.Wrapping ||
          errorState === InitialActionButtonState.Unwrapping
        ) {
          if (!isProcessingRequestRef.current) {
            isProcessingRequestRef.current = true;
            void confirmWrapOrUnwrap();
          }
        } else {
          if (!isProcessingRequestRef.current) {
            isProcessingRequestRef.current = true;
            signOrder().catch(() => {
              isProcessingRequestRef.current = false;
            });
          }
        }
      }
    } else if (errorState) {
      if (errorState === InitialActionButtonState.WrappingQuote) {
        GlobalStore.update(s => {
          s.orderStatus = OrderStatus.WRAPPING_QUOTE;
        });
      }
      GlobalStore.update(s => {
        s.modalSelector = BUTTON_MODAL_MAP[errorState];
      });
    }
  }, [
    errorState,
    state.quote,
    state.allowances,
    state.permitType,
    state.tokenLists,
    isApprovalNeeded,
    confirmWrapOrUnwrap,
    signOrder,
  ]);

  const getDisabledAtr = useCallback(() => {
    if (errorState) {
      return BUTTON_DISABLED_MAP[errorState];
    }
    return BUTTON_DISABLED_MAP.PlaceTrade;
  }, [errorState]);

  const getButtonIcon = useCallback(() => {
    if (errorState === InitialActionButtonState.WalletNotConnected) {
      return (
        <WalletIcon
          css={css`
            margin-right: 4px;
          `}
        />
      );
    }
    if (errorState === InitialActionButtonState.ConfirmingOrder) {
      return <LoadingEllipse />;
    } else if (errorState === InitialActionButtonState.InProgress) {
      return (
        <Spinner
          isVisible={true}
          spinnerSize={20}
          css={css`
            margin-right: 4px;
          `}
        />
      );
    }
    return null;
  }, [errorState]);

  return (
    <ConnectButton.Custom>
      {({ account, chain, openConnectModal, mounted }) => {
        const ready = mounted;
        const connected = ready && account && chain;
        if (!ready) return;
        return (
          <StyledInitialActionButton
            data-testid="action-btn"
            onClick={!connected ? openConnectModal : handleOnClick}
            isDisabled={getDisabledAtr()}
            isWaiting={
              errorState === InitialActionButtonState.ConfirmingOrder ||
              errorState === InitialActionButtonState.InProgress
            }
            buttonType="primary"
          >
            {getButtonIcon()}
            <Text size="Large" textStyle="Button" data-testid="action-btn-text">
              {buttonText}
            </Text>
          </StyledInitialActionButton>
        );
      }}
    </ConnectButton.Custom>
  );
});

const StyledInitialActionButton = styled(Button)`
  border-radius: 14px;
  display: flex;
  padding: 16px 18px 16px 12px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  align-self: stretch;
`;
